import { Button } from '@talentmesh/ui/components/button';
import { FieldGroup } from '@talentmesh/ui/components/field';
import { RHFInput } from '@talentmesh/ui/components/rhf-input';
import { Separator } from '@talentmesh/ui/components/separator';
import { Spinner } from '@talentmesh/ui/components/spinner';
import { TypographyH2, TypographyH3, TypographyP } from '@talentmesh/ui/components/typography';
import { FormProvider } from 'react-hook-form';
import { useUpdateRecruiterSignature } from '../../api/useRecruiterSignature';
import ContactSupportSection from '../../Components/ContactSupportSection';
import ProfileSignatureRTE from './ProfileSignatureRTE';
import { useProfileInfoForm } from './useProfileInfoForm';
import { useNotificationContext } from '@/Context/NotificationContext';
import { useUserContext } from '@/Context/UserContext';
import User from '@/Models/User';
import UIStrings from '@/Utils/UIStrings';

interface AccountInfoFormInitialValues {
    firstName: string;
    lastName: string;
    signature: string;
}

const ProfileInfo = () => {
    const { user, updateUser } = useUserContext();
    const { showSuccessToaster, showFailToaster } = useNotificationContext();
    const { trigger: updateSignature } = useUpdateRecruiterSignature();
    const form = useProfileInfoForm();

    const submitHandler = async (values: AccountInfoFormInitialValues) => {
        const updatedAccountInfo: Omit<AccountInfoFormInitialValues, 'signature'> = { ...values };

        try {
            const updatedUser: User = {
                ...user,
                ...updatedAccountInfo,
            };

            await updateUser(updatedUser);
            await updateSignature({ signature: values.signature });

            showSuccessToaster(UIStrings.AccountDataSuccessfullyUpdated);
        } catch {
            showFailToaster();
        }
    };

    return (
        <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(submitHandler)}>
                <div className="flex flex-col gap-6">
                    <TypographyH2 className="leading-none">{UIStrings.PersonalInformation}</TypographyH2>
                    <FieldGroup className="max-w-2xl gap-4">
                        <RHFInput
                            id="firstName"
                            name="firstName"
                            type="text"
                            label={UIStrings.FirstNameWithAesterisk}
                            fullWidth
                        />
                        <RHFInput
                            id="lastName"
                            name="lastName"
                            type="text"
                            label={UIStrings.LastNameWithAesterisk}
                            fullWidth
                        />
                    </FieldGroup>
                </div>
                <div data-cy="RecruiterSignatureSection" className="mt-6 flex flex-col gap-1.5">
                    <TypographyH3>{UIStrings.Signature}</TypographyH3>
                    <TypographyP>{UIStrings.ThisSignatureWillBeAddedAutomaticallyToAllOutreachEmails}</TypographyP>
                    <div className="mt-4 max-w-4xl">
                        <ProfileSignatureRTE />
                    </div>
                    <Button disabled={!form.formState.isValid} className="mt-4 mb-6 max-w-max" type="submit">
                        {form.formState.isSubmitting && <Spinner className="mr-2 size-4" />}
                        {UIStrings.Save}
                    </Button>
                    <Separator className="my-2" />
                    <ContactSupportSection />
                </div>
            </form>
        </FormProvider>
    );
};

export default ProfileInfo;
